import { useDispatch, useSelector } from 'react-redux'
import { setSelectedSecondIndicator } from './indicatorProfileSlice'
import { getAPIHeaders, getEndpointFromConfig } from '../common/utils/helper'
import { QueryParams, endpointLabels } from '../common/utils/enum'
import fetchData from '../service/apiService'
import { useEffect, useRef, useState } from 'react'
import { CORRELATION_NONE_INDICATOR_VALUE } from '../common/utils/constants'
import useCloseOnOutsideClick from '../common/hooks/useCloseOnOutsideClick'
import useQueryParams from '../common/hooks/useQueryParams'

const SecondIndicatorSelection = () => {
  const {
    endpointList,
    apiKeyName,
    apiKeyValue,
    featureProps: { site },
  } = useSelector((state) => state.common)
  const { selectedSecondIndicator } = useSelector(
    (state) => state.indicatorProfile
  )
  const resultLimit = 10
  const [showDropdown, setShowDropdown] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [pageNo, setPageNo] = useState(0)
  const [hasMoreResults, setHasMoreResults] = useState(true)

  const secondIndicatorListRef = useRef(null)
  const debounceTimer = useRef(null)
  const dispatch = useDispatch()
  const { setQueryParam } = useQueryParams()

  useCloseOnOutsideClick(secondIndicatorListRef, () =>
    closeSecondIndicatorList()
  )

  useEffect(() => {
    if (searchResults.length > 0) {
      const listBox = document.getElementById('cb1-listbox')
      if (listBox) {
        listBox.addEventListener('scroll', handleScroll)
        return () => listBox.removeEventListener('scroll', handleScroll)
      }
    }
  }, [searchResults])

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [])

  const closeSecondIndicatorList = () => {
    setShowDropdown(false)
  }

  const handleSecondIndicatorChange = (indicator) => {
    dispatch(setSelectedSecondIndicator(indicator))
    setQueryParam(QueryParams.SecondIndicator, indicator.id)
    setShowDropdown(false)
  }

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.target
    if (scrollHeight - scrollTop === clientHeight && hasMoreResults) {
      fetchIndicators(searchTerm, pageNo + 1, 'scroll')
      setPageNo(pageNo + 1)
    }
  }

  const handleSearch = (event) => {
    const searchInput = event.target.value
    setSearchTerm(searchInput)
    setHasMoreResults(true)
    setPageNo(0)
    const listBox = document.getElementById('cb1-listbox')
    if (listBox) {
      listBox.scrollTop = 0
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    debounceTimer.current = setTimeout(() => {
      if (searchInput && searchInput.length >= 3) {
        fetchIndicators(searchInput, 0)
      } else if (!searchInput) {
        fetchIndicators('', 0)
      }
    }, 500)
  }

  const fetchIndicators = async (searchText, page, action) => {
    try {
      const request = {
        site,
        data_classification: ['public'],
        entity_name: searchText || '',
        types: ['indicator'],
        items_per_page: resultLimit,
        skip: page * resultLimit,
      }
      const headers = getAPIHeaders({ apiKeyName, apiKeyValue })
      const response = await fetchData(
        getEndpointFromConfig(endpointList, endpointLabels.YouMightAlsoLike)
          .apiEndpoint,
        'POST',
        request,
        headers
      )
      if (response?.results?.length) {
        const indicatorData = response?.results?.map((item) => {
          return {
            id: item?.idno,
            name: item?.name,
            datasetID: item?.databases?.[0]?.idno,
            datasetName: item?.databases?.[0]?.name,
          }
        })
        if (action && action === 'scroll') {
          setSearchResults((prevOptions) => [
            CORRELATION_NONE_INDICATOR_VALUE,
            ...prevOptions.filter(
              (item) => item.id !== CORRELATION_NONE_INDICATOR_VALUE?.id
            ),
            ...indicatorData,
          ])
        } else {
          const results = [CORRELATION_NONE_INDICATOR_VALUE, ...indicatorData]
          setSearchResults(results)
        }
      } else if (response.value.length === 0 && page === 0) {
        setSearchResults([CORRELATION_NONE_INDICATOR_VALUE])
      }
      if (action && action === 'scroll' && response.value.length === 0) {
        setHasMoreResults(false)
      }
    } catch (error) {
      console.error('Error fetching second indicator list')
    }
  }

  return (
    <div className="combobox-list" ref={secondIndicatorListRef}>
      <label htmlFor="cb1-input">Second indicator (Y-axis)</label>
      <div className="">
        <button
          id="cb1-button"
          tabIndex="-1"
          aria-label="States"
          aria-expanded="false"
          aria-controls="cb1-listbox"
          onClick={() => {
            setShowDropdown((prevOption) => !prevOption)
            fetchIndicators(searchTerm, 0)
          }}
        >
          {selectedSecondIndicator?.name}
        </button>
      </div>
      {showDropdown && (
        <div className="combo-items-list">
          <input
            id="cb1-input"
            className="cb_edit"
            type="text"
            role="combobox"
            placeholder="Search indicator name"
            aria-autocomplete="list"
            autoComplete="off"
            aria-expanded="false"
            aria-controls="cb1-listbox"
            aria-activedescendant=""
            onChange={(event) => handleSearch(event)}
            value={searchTerm}
          />
          <ul id="cb1-listbox" aria-label="States">
            {searchResults.map((item, index) => (
              <li
                key={`${item.id}_${index}`}
                onClick={() => handleSecondIndicatorChange(item)}
              >
                {item.name}

                <div className="clsTextDataset">
                  {(item?.datasetName || item?.datasetID) && (
                    <span>
                      {`Dataset : ${item.datasetName || item.datasetID}`}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
export default SecondIndicatorSelection
